import type { ReactNode, Ref } from 'react';
import type { ColumnDef, OnChangeFn, RowData, SortingState } from '@tanstack/react-table';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { Pagination } from './Pagination';

declare module '@tanstack/react-table' {
  // Per-column presentation hints consumed by PagedTable. The generic params
  // are required by the base interface signature.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'left' | 'center' | 'right';
    width?: number | string;
  }
}

/** The page envelope every list endpoint returns. */
export interface PageEnvelope<T> {
  items: T[];
  total: number;
  take: number;
  skip: number;
}

export interface PagedTableProps<T> {
  /** TanStack column definitions. Use `meta.align` / `meta.width` for layout. */
  columns: ColumnDef<T>[];
  /** The current page envelope. `undefined` on the very first load. */
  page: PageEnvelope<T> | undefined;
  /** Show skeleton rows instead of data. */
  isLoading?: boolean;
  /** Render the `errorState` slot instead of data. */
  isError?: boolean;
  /** Fired with the new `skip` offset when the page changes. */
  onPageChange: (skip: number) => void;
  /** Fired with the new `take` when the page size changes. Omit to hide the selector. */
  onPageSizeChange?: (take: number) => void;
  /** Choices for the rows-per-page selector. */
  pageSizeOptions?: number[];
  /** Controlled server-side sorting state. */
  sorting?: SortingState;
  /** Fired when a sortable header is toggled (drives a server re-query). */
  onSortingChange?: OnChangeFn<SortingState>;
  /** Makes rows clickable (drill-down navigation). */
  onRowClick?: (row: T) => void;
  /** Stable row id (defaults to the row index). */
  getRowId?: (row: T, index: number) => string;
  /** Slot shown when there are no rows. */
  emptyState?: ReactNode;
  /** Slot shown when `isError` is true. */
  errorState?: ReactNode;
  /** Number of skeleton rows while loading (defaults to `take` or 5). */
  skeletonRows?: number;
  /** Accessible label for the table. */
  ariaLabel?: string;
  /** Label for the rows-per-page selector. */
  rowsPerPageLabel?: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Server-paged data grid on TanStack Table v8 + MUI Table. Headers render
 * UPPERCASE with a sort affordance (server-driven via `sorting`/
 * `onSortingChange`); rows can be made clickable for drill-downs. Loading shows
 * skeleton rows; the empty and error states render caller-supplied slots.
 *
 * Sorting and pagination are both manual — the component reflects the page
 * envelope and reports intent through callbacks; the query hook owns the data.
 * Per-cell links are supplied by the column's `cell` renderer (e.g. an MUI
 * `Link`), keeping this primitive routing-agnostic.
 */
export function PagedTable<T>({
  columns,
  page,
  isLoading = false,
  isError = false,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  sorting,
  onSortingChange,
  onRowClick,
  getRowId,
  emptyState,
  errorState,
  skeletonRows,
  ariaLabel,
  rowsPerPageLabel,
  ref,
}: PagedTableProps<T>) {
  const data = page?.items ?? [];
  const total = page?.total ?? 0;
  const take = page?.take ?? pageSizeOptions?.[0] ?? 10;
  const skip = page?.skip ?? 0;

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table's useReactTable returns non-memoizable functions by design; the React Compiler safely skips this component and the table is re-created each render as intended.
  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: total,
    enableSorting: !!onSortingChange,
    state: { sorting: sorting ?? [] },
    onSortingChange,
    getRowId,
  });

  const columnCount = columns.length || 1;
  const rows = table.getRowModel().rows;
  const showEmpty = !isLoading && !isError && rows.length === 0;
  const skeletonCount = skeletonRows ?? (take > 0 ? Math.min(take, 8) : 5);

  return (
    <Paper ref={ref} variant="outlined" sx={{ overflow: 'hidden', borderRadius: '4px' }}>
      <TableContainer>
        <Table stickyHeader size="small" aria-label={ariaLabel}>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const align = header.column.columnDef.meta?.align ?? 'left';
                  const width = header.column.columnDef.meta?.width;
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableCell
                      key={header.id}
                      align={align}
                      sortDirection={sorted === false ? false : sorted}
                      sx={{
                        width,
                        p: '10px 14px',
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        bgcolor: 'tableHeaderBg',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <TableSortLabel
                          active={sorted !== false}
                          direction={sorted === false ? 'asc' : sorted}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </TableSortLabel>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {isError ? (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ border: 0 }}>
                  {errorState}
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              Array.from({ length: skeletonCount }).map((_, r) => (
                <TableRow key={`skeleton-${r}`}>
                  {columns.map((column, c) => (
                    <TableCell key={column.id ?? c} align={column.meta?.align ?? 'left'}>
                      <Skeleton variant="text" width={c === 0 ? '70%' : '45%'} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : showEmpty ? (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ border: 0 }}>
                  {emptyState}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover={!!onRowClick}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row.original);
                          }
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'button' : undefined}
                  sx={{
                    '&:nth-of-type(even)': { bgcolor: 'background.default' },
                    ...(onRowClick ? { cursor: 'pointer' } : {}),
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      align={cell.column.columnDef.meta?.align ?? 'left'}
                      sx={{
                        p: '13px 14px',
                        borderBottom: '1px solid',
                        borderColor: 'borderSubtle',
                        fontVariantNumeric:
                          cell.column.columnDef.meta?.align === 'right' ? 'tabular-nums' : 'normal',
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {page && !isError && total > 0 && (
        <Pagination
          total={total}
          take={take}
          skip={skip}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={pageSizeOptions}
          rowsPerPageLabel={rowsPerPageLabel}
        />
      )}
    </Paper>
  );
}
